import { humanise } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { HelperText01, Long01, Short01 } from 'module/ui/components/styles';
import {
  faChevronLeft,
  faChevronRight,
  faGlobe,
} from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import {
  AccountPeopleProfile,
  Icebreaker,
} from 'api/account-people/profile-type';
import { SourceType } from 'api/page-template/types';
import moment from 'moment';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Collapsible, EmptyContent } from './collapsible-section';
import { TextWithCitations } from '../text-with-citations';

const Card = styled.div`
  background: var(--scarlet-gray-10);
  border-radius: var(--border-radius-small);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;

const SlideViewport = styled.div`
  overflow: hidden;
  width: 100%;
  min-height: 70px;
`;

const SlideRow = styled.div<{ index: number }>`
  display: flex;
  transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
  transform: ${({ index }) => `translateX(-${index * 100}%)`};
`;

const Slide = styled.div`
  flex: 0 0 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-04);
  padding: var(--spacing-05) var(--spacing-05) 0;
  justify-content: space-between;
`;

const IcebreakerText = styled.div`
  ${Long01};
  font-size: var(--font-size-base) !important;
  font-weight: var(--font-weight-regular) !important;
  line-height: var(--line-height-02) !important;
  letter-spacing: var(--letter-spacing-01) !important;
  p {
    ${Long01};
    font-size: var(--font-size-base) !important;
    font-weight: var(--font-weight-regular) !important;
    line-height: var(--line-height-02) !important;
    letter-spacing: var(--letter-spacing-01) !important;
  }
`;

const SourceRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

const SourceIcon = styled(FontAwesomeIcon)`
  color: var(--icon-secondary);
`;

const SourceText = styled.span`
  ${HelperText01};
  color: var(--text-tertiary);
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-03) var(--spacing-05);
  border-top: 1px solid var(--divider-default);
`;

const IndexText = styled.span`
  ${Short01};
  color: var(--text-tertiary);
`;
const NavWrapper = styled.div`
  display: flex;
  align-items: center;
  .rl-btn svg {
    color: var(--icon-secondary);
  }
`;

export interface IceBreakerSliderProps {
  icebreakers: Icebreaker[];
}
const getSourceTypeText = (sourceType: SourceType, sourceTime: string) => {
  const time = humanise(moment(sourceTime));

  switch (sourceType) {
    case SourceType.Meeting:
      return (
        <FormattedMessage
          id="account.people.profile.sections.ice_breakers.source_type.meeting"
          values={{ time }}
        />
      );
    case SourceType.Email:
      return (
        <FormattedMessage
          id="account.people.profile.sections.ice_breakers.source_type.email"
          values={{ time }}
        />
      );
    default:
      return '';
  }
};

export const IceBreakerSlider: React.FC<IceBreakerSliderProps> = ({
  icebreakers,
}) => {
  const [index, setIndex] = useState(0);
  const total = icebreakers.length;

  return (
    <div>
      <Card>
        <SlideViewport>
          <SlideRow index={index}>
            {icebreakers.map((item, idx) => (
              <Slide key={idx}>
                <IcebreakerText>
                  <TextWithCitations
                    value={item.icebreaker}
                    citationMapping={item.citationMapping}
                  />
                </IcebreakerText>
                <SourceRow>
                  <SourceIcon icon={faGlobe} size={12} />
                  <SourceText>
                    {getSourceTypeText(item.sourceType, item.sourceTime)}
                  </SourceText>
                </SourceRow>
              </Slide>
            ))}
          </SlideRow>
        </SlideViewport>
        <SliderRow>
          <IndexText>
            <FormattedMessage
              id="account.people.profile.sections.ice_breakers.index"
              values={{ index: index + 1, total }}
            />
          </IndexText>
          <NavWrapper>
            <Button
              variant="ghost"
              size="small"
              onClick={() => setIndex((i) => Math.max(i - 1, 0))}
              disabled={index === 0}
              aria-label="Previous"
              leftIcon={<FontAwesomeIcon icon={faChevronLeft} size={12} />}
            />
            <Button
              variant="ghost"
              size="small"
              onClick={() => setIndex((i) => Math.min(i + 1, total - 1))}
              disabled={index === total - 1}
              aria-label="Next"
              leftIcon={<FontAwesomeIcon icon={faChevronRight} size={12} />}
            />
          </NavWrapper>
        </SliderRow>
      </Card>
    </div>
  );
};
interface Props {
  userProfile: AccountPeopleProfile;
}

export const IceBreakers: React.FC<Props> = ({
  userProfile: { userEngagementData },
}) => {
  const icebreakers = userEngagementData?.icebreakers;
  const isEmpty = icebreakers == null || icebreakers.length === 0;

  return (
    <Collapsible
      title={
        <FormattedMessage id="account.people.profile.sections.ice_breakers.title" />
      }
      panelKey="ice-breakers"
    >
      {isEmpty ? (
        <EmptyContent>
          <FormattedMessage id="account.people.profile.sections.ice_breakers.empty" />
        </EmptyContent>
      ) : (
        <IceBreakerSlider icebreakers={icebreakers} />
      )}
    </Collapsible>
  );
};
